import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent,
} from 'firebase/analytics';
import { getApp } from 'firebase/app';

export enum GoogleAnalyticsEventName {
  Approve = 'ADMIN Request Approved',
  Reject = 'ADMIN Request Rejected',
  Cancel = 'ADMIN Request Canceled',
  EditAdmin = 'ADMIN Request Edited',
  EditUser = 'USER Request Edited',
  MessageSent = 'CHAT message sent',
  WorkNoteSent = 'WORK NOTE message sent',
  CreateEvent = 'Created new event',
  DeleteEvent = 'Deleted event',
  LoginSuccessful = 'Login Successful',
  GoogleCalendarBooking = 'Book via Google Calendar',
  ExternalBooking = 'External Booking',
}

export enum GoogleAnalyticsEventCategory {
  BookingRequest = 'Booking Request',
  GoogleCalendarBooking = 'Google Calendar Booking',
  ExternalServiceBooking = 'External Service Booking',
  Login = 'Login',
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  readonly #router = inject(Router, { optional: true });
  #analyticsPromise: Promise<Analytics | null> | null = null;

  constructor() {
    this.#trackScreenViews();
  }

  async #getAnalytics(): Promise<Analytics | null> {
    if (!this.#analyticsPromise) {
      this.#analyticsPromise = this.#createAnalyticsInstance();
    }

    const analytics = await this.#analyticsPromise;
    if (!analytics) {
      // Allow a future retry if app initialization order caused a transient failure.
      this.#analyticsPromise = null;
    }

    return analytics;
  }

  async #createAnalyticsInstance(): Promise<Analytics | null> {
    try {
      const supported = await isSupported();
      return supported ? getAnalytics(getApp()) : null;
    } catch (error) {
      console.warn('Google Analytics initialization failed.', error);
      return null;
    }
  }

  #trackScreenViews() {
    if (!this.#router) {
      return;
    }

    this.#router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }

      void this.#logScreenView(event.urlAfterRedirects);
    });
  }

  async #logScreenView(url: string) {
    const analytics = await this.#getAnalytics();
    if (!analytics) {
      return;
    }

    logEvent(analytics, 'screen_view', {
      firebase_screen: url,
      firebase_screen_class: 'AngularRoute',
    });
  }

  trackEvent(
    eventName: GoogleAnalyticsEventName,
    eventCategory: GoogleAnalyticsEventCategory,
    eventDetails: string | number
  ) {
    void this.#getAnalytics().then(analytics => {
      if (!analytics) {
        return;
      }

      logEvent(analytics, eventName, {
        event_category: eventCategory,
        event_label: eventName,
        value: eventDetails,
      });
    });
  }
}
